import { useState, useEffect, useCallback, useRef } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors } from "../../lib/colors"
import StatCard from "../../components/analytics/StatCard"
import BarChart from "../../components/analytics/BarChart"
import InstagramLinkButton from "../../components/analytics/InstagramLinkButton"
import DateRangePicker, { type DateRange } from "../../components/analytics/DateRangePicker"
import { api } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import { downloadCsv, downloadChartAsPng, chartDataToCsvRows, todayStr } from "../../lib/csv"
import type { FanUserDetail, TaggedPost } from "../../types/analytics"
import "../../styles/analytics.scss"

const toDateStr = (d: Date) => d.toISOString().split("T")[0]

const FanUserDetailPage = () => {
  const { owner_id } = useParams<{ owner_id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { selectedAccountId, setSingleAccountOnly } = useSelectedAccount()
  const accountId = parseInt(searchParams.get("account_id") ?? "") || selectedAccountId
  const trendChartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSingleAccountOnly(true)
  }, [setSingleAccountOnly])

  const [detail, setDetail]     = useState<FanUserDetail | null>(null)
  const [loading, setLoading]   = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [range, setRange]       = useState<DateRange>(() => {
    const end = new Date(); const start = new Date()
    start.setDate(start.getDate() - 29)
    return { startDate: toDateStr(start), endDate: toDateStr(end) }
  })

  const fetchData = useCallback(async () => {
    if (!accountId || !owner_id) { setLoading(false); return }
    setLoading(true)
    setNotFound(false)
    try {
      const params = new URLSearchParams({
        instagram_account_id: String(accountId),
        start_date: range.startDate,
        end_date:   range.endDate,
      })
      const res = await api.get<FanUserDetail>(`/analytics/fan-users/${owner_id}?${params}`)
      setDetail(res)
    } catch (e: unknown) {
      const err = e as { status?: number }
      if (err.status === 404) setNotFound(true)
    } finally {
      setLoading(false)
    }
  }, [accountId, owner_id, range])

  useEffect(() => { fetchData() }, [fetchData])

  const trendChartData = detail && detail.trend.length > 0 ? {
    labels:   detail.trend.map(r => r.week),
    datasets: [{
      label:           "投稿数",
      data:            detail.trend.map(r => r.posts_count),
      backgroundColor: colors.primary,
    }],
  } : null

  const p = detail?.profile
  const s = detail?.stats
  const c = detail?.comparison

  return (
    <AppLayout pageTitle={p?.display_name ?? "ファンユーザー詳細"} onBack={() => navigate("/fan-users")}>
      <div className="analytics">
        {/* ヘッダー */}
        <div className="analytics__header" style={{ alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, flexWrap: "wrap" }}>
            {p && (
              <>
                <h2 style={{ margin: 0, fontSize: 18 }}>{p.display_name}</h2>
                {p.username && (
                  <InstagramLinkButton href={`https://www.instagram.com/${p.username}/`} />
                )}
              </>
            )}
          </div>
          <DateRangePicker value={range} onChange={setRange} />
        </div>

        {!selectedAccountId ? (
          <AccountSelectionNotice />
        ) : loading ? (
          <p style={{ color: colors.gray400 }}>読み込み中…</p>
        ) : notFound ? (
          <p style={{ color: colors.gray400 }}>ユーザーが見つかりません</p>
        ) : !detail ? null : (
          <>
            {/* プロフィールカード */}
            <div className="analytics__section">
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 24,
                  display: "flex",
                  gap: 24,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", gap: 16, flex: 1, alignItems: "center" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: colors.gray200, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="bi bi-person" style={{ fontSize: 32, color: colors.gray400 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>{p?.display_name}</div>
                    {p?.username && <div style={{ color: colors.gray400, fontSize: 13 }}>@{p.username}</div>}
                  </div>
                </div>

                {c && (
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    {[
                      { label: "期間投稿数",    fan: c.posts_count,    avg: c.avg_posts_per_fan },
                      { label: "平均いいね",    fan: c.avg_likes,      avg: c.overall_avg_likes },
                      { label: "平均コメント",  fan: c.avg_comments,   avg: c.overall_avg_comments },
                    ].map(item => (
                      <div
                        key={item.label}
                        style={{ background: "#f9fafb", borderRadius: 8, padding: "12px 16px", minWidth: 120, textAlign: "center" }}
                      >
                        <div style={{ fontSize: 11, color: colors.gray400, marginBottom: 4 }}>{item.label}</div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: colors.gray900 }}>
                          {item.fan != null ? item.fan.toLocaleString() : "—"}
                        </div>
                        <div style={{ fontSize: 12, color: colors.gray400, marginTop: 2 }}>
                          平均: {item.avg != null ? item.avg.toLocaleString() : "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* StatCard 3枚 */}
            <div className="analytics__stats-grid">
              <StatCard
                label="期間投稿数"
                stat={{ value: s?.posts_count ?? null, change_rate: null }}
              />
              <StatCard
                label="平均いいね"
                stat={{ value: s?.avg_likes ?? null, change_rate: null }}
              />
              <StatCard
                label="平均コメント"
                stat={{ value: s?.avg_comments ?? null, change_rate: null }}
              />
            </div>

            {/* 週次推移 */}
            <div className="analytics__section">
              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">週次投稿推移</span>
                  {trendChartData && (
                    <div className="chart-card__actions">
                      <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(trendChartData), `fan_user_trend_${todayStr()}.csv`)}>↓ CSV</button>
                      <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(trendChartRef.current, `fan_user_trend_${todayStr()}.png`)}>↓ PNG</button>
                    </div>
                  )}
                </div>
                <div className="chart-card__canvas-wrap" ref={trendChartRef}>
                  {trendChartData
                    ? <BarChart data={trendChartData} />
                    : <p style={{ color: colors.gray400, fontSize: 13 }}>この期間の投稿はありません</p>
                  }
                </div>
              </div>
            </div>

            {/* 全体平均との比較 */}
            <div className="analytics__section">
              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">期間サマリー比較</span>
                  {c && (
                    <button
                      className="summary-table__csv-btn"
                      onClick={() => downloadCsv(
                        [
                          ["指標", "このユーザー", "全体平均"],
                          ["投稿数", c.posts_count, c.avg_posts_per_fan],
                          ["いいね合計", s?.likes_count ?? null, null],
                          ["コメント合計", s?.comments_count ?? null, null],
                          ["平均いいね", c.avg_likes, c.overall_avg_likes],
                          ["平均コメント", c.avg_comments, c.overall_avg_comments],
                        ],
                        `fan_user_summary_${todayStr()}.csv`
                      )}
                    >
                      ↓ CSV
                    </button>
                  )}
                </div>
                <div style={{ padding: "0 16px 16px" }}>
                  <table style={{ width: "100%", fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: colors.gray400, borderBottom: "1px solid #e5e7eb" }}>
                        <th style={{ textAlign: "left", padding: "8px 4px" }}>指標</th>
                        <th style={{ textAlign: "right", padding: "8px 4px" }}>このユーザー</th>
                        <th style={{ textAlign: "right", padding: "8px 4px" }}>全体平均</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: "投稿数",      fan: c?.posts_count,    avg: c?.avg_posts_per_fan },
                        { label: "いいね合計",   fan: s?.likes_count,    avg: null },
                        { label: "コメント合計", fan: s?.comments_count, avg: null },
                        { label: "平均いいね",   fan: c?.avg_likes,      avg: c?.overall_avg_likes },
                        { label: "平均コメント", fan: c?.avg_comments,   avg: c?.overall_avg_comments },
                      ].map(row => (
                        <tr key={row.label} style={{ borderBottom: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "8px 4px", color: colors.gray700 }}>{row.label}</td>
                          <td style={{ padding: "8px 4px", textAlign: "right", fontWeight: 500 }}>
                            {row.fan != null ? row.fan.toLocaleString() : "—"}
                          </td>
                          <td style={{ padding: "8px 4px", textAlign: "right", color: colors.gray400 }}>
                            {row.avg != null ? row.avg.toLocaleString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* 投稿一覧 */}
            <div className="analytics__section">
              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">投稿一覧</span>
                </div>

                {(["tagged_posts", "mentioned_posts"] as const).map((key) => {
                  const posts = detail[key]
                  const label = key === "tagged_posts" ? "タグ付された投稿" : "メンションされた投稿"
                  return (
                    <div key={key}>
                      <div style={{ padding: "12px 16px 8px", fontSize: 13, fontWeight: 600, color: colors.gray700 }}>
                        {label}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, padding: "0 16px 16px" }}>
                        {posts.length === 0 ? (
                          <p style={{ color: colors.gray400, fontSize: 13, gridColumn: "span 6" }}>この期間の投稿はありません</p>
                        ) : (
                          posts.map((post: TaggedPost, i: number) => (
                            <a
                              key={i}
                              href={post.permalink ?? "#"}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: "block", textDecoration: "none" }}
                            >
                              <div style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "hidden", background: colors.gray200 }}>
                                {post.media_url ? (
                                  <img src={post.media_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: colors.gray400, fontSize: 20 }}>
                                    <i className="bi bi-image" />
                                  </div>
                                )}
                                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.5)", color: "#fff", fontSize: 11, padding: "4px 6px", display: "flex", gap: 8 }}>
                                  <span>♥ {post.like_count.toLocaleString()}</span>
                                  <span>💬 {post.comments_count.toLocaleString()}</span>
                                </div>
                              </div>
                            </a>
                          ))
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default FanUserDetailPage
